// ** React Imports
import { Fragment, useEffect, useMemo, useState } from "react";
import { getToken } from "../../../utils/auth";
// ** MUI Components
import {
  IconButton,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  MenuItem,
  FormHelperText
} from "@mui/material";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Divider from "@mui/material/Divider";
import { styled, useTheme } from "@mui/material/styles";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { DateTimePicker } from '@mui/x-date-pickers-pro';


import dayjs from 'dayjs';

// Custom Component Imports
import CustomTextField from '../../../components/mui/text-field';
import CustomAutocomplete from "components/mui/autocomplete";
import UnsavedChangesDialog from '../../../components/UnsavedChangesDialog';

// ** Icon Imports
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import Icon from '../../../components/icon'
import { GetServiceRequests } from "../../Requests/logic/GetServiceRequest"
import { dev_baseUrl } from "utils/var";

const AddCommunicationLog = ({
    open,
    handleClose,
    customerID,
    fetchLogs,
    logData,
    date,
    contacts,
}) => {

    // ** Hooks
    const theme = useTheme();

    const [communicationType, setCommunicationType] = useState(logData?.communicationType || "Email");
    const [description, setDescription] = useState(logData?.description || "");
    const [communicationDateTime, setCommunicationDateTime] = useState("");
    const [recordType, setRecordType] = useState(logData?.record || "Customer");
    const [recordID, setRecordID] = useState(null);
    const [followUpReminderTime, setFollowUpReminderTime] = useState(null);
    const [selectedFollowUp, setSelectedFollowUp] = useState('');
    const [errors, setErrors] = useState({});
    const [contactID, setContactID] = useState(null);
    const [selectedContact, setSelectedContact] = useState(null);
    const [loading, setLoading] = useState(false);
    const [requests, setRequests] = useState([]);
    // const [dropdownValue, setDropdownValue] = useState(null); // Value for CustomAutocomplete

    // console.log(logData);

    const isEdit = !!logData;
    const [initialized, setInitialized] = useState(false);
    const [dropdownValue, setDropdownValue] = useState(null);
    
    // Unsaved changes tracking
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const [initialValuesCaptured, setInitialValuesCaptured] = useState(false);

  const normalizeType = t => (t || '').toLowerCase();

  const buildLabel = (type, id) => {
    switch (normalizeType(type)) {
      case 'request':    return `REQ-${id}`;
      case 'assessment': return `AST-${id}`;
      case 'estimate':   return `EST-${id}`;
      case 'workorder':  return `WO-${id}`;
      case 'invoice':    return `INV-${id}`;
      case 'customer':   return String(id);
      default:           return String(id);
    }
  };

    const calculateNewFollowUpTime = (duration) => {
      setSelectedFollowUp(duration);
      const now = dayjs(); // Use the current date and time
      let newDate;
      switch (duration) {
        case '1_week':
          newDate = now.add(7, 'day');
          break;
        case '2_weeks':
          newDate = now.add(14, 'day');
          break;
        case '1_month':
          newDate = now.add(1, 'month');
          break;
        default:
          newDate = now;
          break;
      }
      return newDate.format('YYYY-MM-DDTHH:mm');
    };

    useEffect(() => {
      if (!open) {
        // Reset when dialog closes
        setInitialized(false);
        setInitialValuesCaptured(false);
        return;
      }
    
      if (isEdit) {
        const initialCommType = logData?.communicationType ?? 'Email';
        const initialDesc = logData?.description ?? '';
        const initialCommDateTime = logData?.date ? dayjs(logData.date).format('YYYY-MM-DDTHH:mm') : '';
        const initialRT = logData?.record ?? 'Customer';
        const initialRecID = logData?.recordid ?? (normalizeType(initialRT) === 'customer' ? customerID : null);
        const initialContID = logData?.contactID ?? null;
        const initialFollowUp = logData?.followUp ? dayjs(logData.followUp).format('YYYY-MM-DDTHH:mm') : null;
        
        setCommunicationType(initialCommType);
        setDescription(initialDesc);
        setCommunicationDateTime(initialCommDateTime);
        setRecordType(initialRT);
        setRecordID(initialRecID);
        setContactID(initialContID);
        setFollowUpReminderTime(initialFollowUp);
      } else {
        setCommunicationType('Email');
        setDescription('');
        setCommunicationDateTime(date ? dayjs(date).format('YYYY-MM-DDTHH:mm') : dayjs().format('YYYY-MM-DDTHH:mm'));
        setRecordType('Customer');
        setRecordID(customerID);
        setFollowUpReminderTime(null);
        setSelectedFollowUp('');
        setSelectedContact(null);
        setContactID(null);
      }
    
      setInitialized(true);
    }, [open, isEdit, logData, customerID, date]);
    
    // Capture initial values AFTER all state has been initialized (separate useEffect)
    useEffect(() => {
      if (!open || !initialized || initialValuesCaptured) return;
      
      // Small delay to ensure all other useEffects have run and state is stable
      const timer = setTimeout(() => {
        setInitialValues({
          communicationType,
          description,
          communicationDateTime,
          recordType,
          recordID,
          contactID,
          followUpReminderTime
        });
        setInitialValuesCaptured(true);
      }, 100);
      
      return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized, open, initialValuesCaptured]);

    useEffect(() => {
      if (logData && contacts.length > 0) {
        // console.log('Looking for contactID:', logData.contactID);
        const matchedContact = contacts.find(contact => contact.id === logData.contactID);
        // console.log('Matched Contact:', matchedContact);
        if (matchedContact) {
          setSelectedContact({ label: matchedContact.contact, value: matchedContact.id });          
        } else {
          setSelectedContact(null);
        }
      } else {
        setSelectedContact(null);
      }
    }, [logData, contacts]);
    
  useEffect(() => {    
    fetchRequests(customerID,0);
  }, [customerID]);

  const fetchRequests = async (customerID, requestID) => {
    
      try {
        setLoading(true);
        let requests = await GetServiceRequests(customerID, requestID);
        // console.log(requests)
        
        setRequests(requests);       

      } catch (error) {
        console.error("Error fetching Requests data:", error);
      } finally {
        setLoading(false);
      }
  };

  const recordIDOptions = useMemo(() => {
    const rt = normalizeType(recordType);
  
    if (rt === 'customer') {
      return [{ label: customerID?.toString() ?? '', value: customerID }];
    }
  
    // Invoice is read-only: don’t provide selectable options.
    if (rt === 'invoice') {
      if (isEdit && logData?.recordid) {
        return [{ label: buildLabel('Invoice', logData.recordid), value: logData.recordid }];
      }
      return [];
    }
  
    return requests
      .filter(item => normalizeType(item.entityType) === rt)
      .map(item => ({
        label: buildLabel(item.entityType, item.requestID),
        value: item.requestID
      }));
  }, [recordType, customerID, requests, isEdit, logData?.recordid]);
  
  useEffect(() => {
    if (!initialized) return;
  
    const rt = normalizeType(recordType);
  
    if (rt === 'customer') {
      const opt = { label: customerID?.toString() ?? '', value: customerID };
      setDropdownValue(opt);
      setRecordID(customerID);
      return;
    }
  
    if (rt === 'invoice') {
      // No dropdown. Just ensure recordID is correct.
      if (isEdit && logData?.recordid) {
        setRecordID(logData.recordid);
      }
      setDropdownValue(null);
      return;
    }
  
    const wantedId = recordID ?? logData?.recordid ?? null;
    const match = recordIDOptions.find(o => o.value === wantedId);
    // Only update if the value actually changed (not object reference)
    if (match?.value !== dropdownValue?.value) {
      setDropdownValue(match ?? null);
    }
  }, [initialized, recordType, recordIDOptions, customerID, recordID]);

    // Check if there are unsaved changes
    const hasUnsavedChanges = () => {
      if (!initialized || !initialValuesCaptured) return false;
      
      const changed = (
        communicationType !== initialValues.communicationType ||
        description !== initialValues.description ||
        communicationDateTime !== initialValues.communicationDateTime ||
        recordType !== initialValues.recordType ||
        String(recordID) !== String(initialValues.recordID) ||  // Compare as strings to handle type mismatch
        contactID !== initialValues.contactID ||
        followUpReminderTime !== initialValues.followUpReminderTime
      );
      
      return changed;
    };

    // Intercept close to check for unsaved changes
    const handleCloseAttempt = () => {
      if (hasUnsavedChanges()) {
        setShowUnsavedDialog(true);
      } else {
        handleResetAndClose();
      }
    };

    // Discard changes and close
    const handleDiscardChanges = () => {
      setShowUnsavedDialog(false);
      handleResetAndClose();
    };

    const handleResetAndClose = () => {
      setCommunicationType("Email");
      setDescription("");
      setCommunicationDateTime("");
      setRecordType("select");
      setRecordID(null);
      setFollowUpReminderTime(null);
      setSelectedFollowUp('');
      setSelectedContact(null);
      setContactID(null);
      setErrors({});
      setInitialValues({});
      setInitialValuesCaptured(false);
      handleClose();
    }

    const handleCreateLog = async () => {
      // console.log(recordID)
      
      const requestBody = {
        communicationLogID: logData ? logData.id : 0,
        customerID: customerID, // Replace with actual customer ID if available
        type: communicationType,
        description,
        entityID: recordID,
        entityType: recordType === "select" ? "Customer" : recordType,
        communicationLogTime: communicationDateTime,
        followUpReminderTime,
        contactID: contactID
      };

      if(description === '') { setErrors((prevState) => ({...prevState, description: `Description is required.`})) } 
      else{ setErrors((prevState) => ({...prevState, description: ''})) }

      if(communicationDateTime === null) { setErrors((prevState) => ({...prevState, communicationDateTime: `Communication Date and Time is required.`})) } 
      else{ setErrors((prevState) => ({...prevState, communicationDateTime: ''})) }

      if(followUpReminderTime === null) { setErrors((prevState) => ({...prevState, followUpReminderTime: `Follow-Up Reminder Time is required.`})) } 
      else{ setErrors((prevState) => ({...prevState, followUpReminderTime: ''})) }

      if(recordType && !recordID) { setErrors((prevState) => ({...prevState, recordID: `Record id is required.`})) } 
      else{ setErrors((prevState) => ({...prevState, recordID: ''})) }

      if(!contactID) { setErrors((prevState) => ({...prevState, contactID: `Contact is required.`})) } 
      else{ setErrors((prevState) => ({...prevState, contactID: ''})) }

      if (communicationType === '' || description === '' || communicationDateTime === '' || followUpReminderTime === '' || !recordID || contactID === '' || !contactID || !followUpReminderTime) {
        toast.error("Please fill all required fields", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
        });
        return; // Exit the function early if validation fails
      }

      const accessToken = getToken();
      try {
        
        
        if (!accessToken) {
          toast.error("Access token is missing. Please log in to proceed.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            style: {
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '15px',
            },
          });
          return; // Exit the function early
        }
      
        const response = await fetch(`${dev_baseUrl}/CommunicationLog/saveCommunicationLog`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`, // Include the token in the Authorization header
          },
          body: JSON.stringify(requestBody),
        });        
      // console.log(requestBody)
        const resData = await response.json();
        // console.log(resData)
        const message = logData
          ? "Communication log updated successfully!"
          : "Communication log created successfully!";
          const errormessage = logData
          ? "Failed to update communication log. Please try again."
          : "Failed to create communication log. Please try again.";

        if (!response.ok) {
          if (response.status === 401) {
            toast.error("You are not authorized to perform this action. Please log in again.", {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",         
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
 
            });
          } else {
            toast.error(resData?.message || errormessage || "Failed to perform this action. Please try again.", {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
            });
          }
          return;
        }
      
        // Success toast notification
        toast.success(resData?.message || message || "Action performed successfully", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
        });

        fetchLogs();
        handleResetAndClose();
      } catch (error) {
        console.error("Error:", error);
        toast.error("An error occurred while performing this action. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '15px',
          },
        });
      }
    };

    return (
      <>
      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={open}
        onClose={handleCloseAttempt}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h3" fontWeight="bold">
              {logData
                ? "Update Communication Log"
                : "Create Communication Log"}
            </Typography>
            <IconButton onClick={handleCloseAttempt}>
              <Icon icon="tabler:x" />
            </IconButton>
          </Box>
        </DialogTitle>
        {/* {
          loading ? (<>p</>) : ( */}
        <DialogContent>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" }, // Stack on mobile, row on desktop
                  gap: { xs: 2, sm: 4 }, // Smaller gap when stacked
                  alignItems: { xs: "flex-start", sm: "center" }, // Align items properly
                }}
              >
                <Box>
                  <IconButton
                    size="large"
                    sx={{ pointerEvents: "none", border: "1px solid #D9D9D9" }}
                  >
                    <Icon icon="tabler:link" />
                  </IconButton>
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    Communication Type{" "}
                    <span style={{ color: theme.palette.error.main }}>*</span>
                  </Typography>
                  <Typography variant="body2">
                    Select whether this communication was via email or call.
                  </Typography>
                  <Box>
                    <FormControl>
                      <RadioGroup
                        row
                        name="cummunicationTypeSelection"
                        value={communicationType}
                        onChange={(e) => setCommunicationType(e.target.value)}
                      >
                        <FormControlLabel
                          value="Email"
                          control={<Radio color="success" />}
                          label="Email"
                          disabled={
                            normalizeType(recordType) === "invoice" ||
                            description === "An Estimate was sent via an Email"
                          }
                        />
                        <FormControlLabel
                          value="Call"
                          control={<Radio color="success" />}
                          label="Call"
                          disabled={
                            normalizeType(recordType) === "invoice" ||
                            description === "An Estimate was sent via an Email"
                          }
                        />
                        <FormControlLabel
                          value="Text Message"
                          control={<Radio color="success" />}
                          label="SMS"
                          disabled={
                            normalizeType(recordType) === "invoice" ||
                            description === "An Estimate was sent via an Email"
                          }
                        />
                        <FormControlLabel
                          value="Mail/Physical Mail"
                          control={<Radio color="success" />}
                          label="Mail"
                          disabled={
                            normalizeType(recordType) === "invoice" ||
                            description === "An Estimate was sent via an Email"
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" fontWeight="bold">
                      Description{" "}
                      <span style={{ color: theme.palette.error.main }}>*</span>
                    </Typography>
                    <CustomTextField
                      placeholder="Enter a brief description of the email or call"
                      disabled={
                        normalizeType(recordType) === "invoice" ||
                        description === "An Estimate was sent via an Email"
                      }
                      fullWidth
                      multiline
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      error={!!errors?.description}
                      helperText={errors?.description}
                    />
                  </Box>
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" fontWeight="bold">
                      Customer Contact{" "}
                      <span style={{ color: theme.palette.error.main }}>*</span>
                    </Typography>

                    <CustomAutocomplete
                      required
                      disabled={
                        normalizeType(recordType) === "invoice" ||
                        description === "An Estimate was sent via an Email"
                      }
                      options={contacts.map((contact) => ({
                        label: contact.contact,
                        value: contact.id,
                      }))}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setSelectedContact(newValue); // Update state with the selected option
                          setContactID(newValue.value || ""); // Update contactID with the selected value
                        } else {
                          setSelectedContact(null);
                          setContactID("");
                        }
                      }}
                      value={selectedContact || { label: "", value: "" }} // Ensure default value
                      id="autocomplete-custom"
                      getOptionLabel={(option) => option?.label || ""} // Ensure safe label access
                      renderInput={(params) => (
                        <CustomTextField
                          fullWidth
                          placeholder="Select Contact"
                          {...params}
                        />
                      )}
                    />

                    <FormHelperText
                      sx={{ fontSize: "13px", color: "error.main" }}
                    >
                      {errors?.contactID}
                    </FormHelperText>
                  </Box>
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" fontWeight="bold">
                      Communication Date & Time{" "}
                      <span style={{ color: theme.palette.error.main }}>*</span>
                    </Typography>
                    <Typography variant="body2">
                      Select the timestamp when this communication occurred.
                      Defaults to the current date and time.
                    </Typography>
                    {/* <CustomTextField 
                                    type='datetime-local'
                                    sx={{mt: 2}}
                                    value={communicationDateTime}
                                    onChange={(e) => setCommunicationDateTime(e.target.value)}
                                    error={!!errors?.communicationDateTime}
                                    helperText={errors?.communicationDateTime}
                                /> */}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        sx={{ width: "100%" }}
                        value={communicationDateTime ? dayjs(communicationDateTime) : null}
                        onChange={(newValue) =>
                          setCommunicationDateTime(
                            newValue ? newValue.format('YYYY-MM-DDTHH:mm') : null
                          )
                        }
                        //minDateTime={dayjs()} // prevent selecting past datetime
                        desktopModeMediaQuery="@media (max-width: 0px)" // always mobile mode
                        disabled={
                          normalizeType(recordType) === "invoice" ||
                          description === "An Estimate was sent via an Email"
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            error: !!errors?.communicationDateTime,
                          },
                        }}
                      />
                    </LocalizationProvider>

                    <FormHelperText
                      sx={{ fontSize: "13px", color: "error.main" }}
                    >
                      {errors?.communicationDateTime}
                    </FormHelperText>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider
                sx={{
                  color: "text.disabled",
                  "& .MuiDivider-wrapper": { px: 6 },
                  fontSize: theme.typography.body2.fontSize,
                  my: (theme) => `${theme.spacing(6)} !important`,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 1, sm: 4 }, // Smaller gap when stacked
                  alignItems: { xs: "flex-start", sm: "center" },
                }}
              >
                <Box>
                  <IconButton
                    size="large"
                    sx={{ pointerEvents: "none", border: "1px solid #D9D9D9" }}
                  >
                    <Icon icon="tabler:link" />
                  </IconButton>
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    Link with Record
                  </Typography>
                  <Typography variant="body2">
                    Select a record to link this communication with for accurate
                    tracking and follow-up.
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      mt: 4,
                    }}
                  >
                    {normalizeType(recordType) === "invoice" ? (
                      <>
                        {/* Read-only Record Type */}
                        <CustomTextField
                          fullWidth
                          disabled
                          value="Invoice"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography>Record Type</Typography>
                                <Divider
                                  orientation="vertical"
                                  sx={{ height: "20px", mx: 6 }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {/* Read-only Record ID */}
                        <CustomTextField
                          fullWidth
                          disabled
                          value={
                            recordID ? buildLabel("Invoice", recordID) : ""
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" sx={{ ml: 2 }}>
                                <Typography>Record ID</Typography>
                                <Divider
                                  orientation="vertical"
                                  sx={{ height: "20px", ml: 10, mr: 4 }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </>
                    ) : (
                      <>
                        {/* Selectable Record Type */}
                        <CustomTextField
                          select
                          fullWidth
                          disabled={
                            normalizeType(recordType) === "invoice" ||
                            description === "An Estimate was sent via an Email"
                          }
                          value={recordType}
                          onChange={(e) => {
                            const next = e.target.value;
                            setRecordType(next);
                            setRecordID(null);
                            setDropdownValue(null);
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography>Record Type</Typography>
                                <Divider
                                  orientation="vertical"
                                  sx={{ height: "20px", mx: 6 }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        >
                          <MenuItem value="Customer">Customer</MenuItem>
                          <MenuItem value="Request">Request</MenuItem>
                          <MenuItem value="Estimate">Estimate</MenuItem>
                          <MenuItem value="Assessment">Assessment</MenuItem>
                          <MenuItem value="WorkOrder">Work Order</MenuItem>
                          {/* No Invoice here */}
                        </CustomTextField>
                        <FormHelperText
                          sx={{ fontSize: "13px", color: "error.main" }}
                        >
                          {errors?.recordType}
                        </FormHelperText>

                        {/* Selectable Record ID */}
                        <CustomAutocomplete
                          fullWidth
                          id="autocomplete-custom"
                          disabled={
                            normalizeType(recordType) === "invoice" ||
                            description === "An Estimate was sent via an Email"
                          }
                          options={recordIDOptions}
                          value={dropdownValue}
                          onChange={(event, newValue) => {
                            setDropdownValue(newValue);
                            setRecordID(newValue?.value ?? null);
                          }}
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option?.value === value?.value
                          }
                          renderInput={(params) => (
                            <CustomTextField
                              {...params}
                              placeholder="Select Record ID"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment
                                    position="start"
                                    sx={{ ml: 2 }}
                                  >
                                    <Typography>Record ID</Typography>
                                    <Divider
                                      orientation="vertical"
                                      sx={{ height: "20px", ml: 10, mr: 4 }}
                                    />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                        <FormHelperText
                          sx={{ fontSize: "13px", color: "error.main" }}
                        >
                          {errors?.recordID}
                        </FormHelperText>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider
                sx={{
                  color: "text.disabled",
                  "& .MuiDivider-wrapper": { px: 6 },
                  fontSize: theme.typography.body2.fontSize,
                  my: (theme) => `${theme.spacing(6)} !important`,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 4 },
                  alignItems: { xs: "flex-start", sm: "center" },
                }}
              >
                <Box>
                  <IconButton
                    size="large"
                    sx={{ pointerEvents: "none", border: "1px solid #D9D9D9" }}
                  >
                    <Icon icon="tabler:bell" />
                  </IconButton>
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    Follow-up Reminder{" "}
                    <span style={{ color: theme.palette.error.main }}>*</span>
                  </Typography>
                  <Typography variant="body2">
                    Select a future date and time to remind you for a follow-up
                    on this communication.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {/* <CustomTextField 
                                    type='datetime-local'
                                    sx={{mt: 2}}
                                    value={followUpReminderTime}
                                    onChange={(e) => setFollowUpReminderTime(e.target.value)}
                                    error={!!errors?.followUpReminderTime}
                                    helperText={errors?.followUpReminderTime}
                                /> */}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        sx={{ width: "100%" }}
                        value={
                          followUpReminderTime
                            ? dayjs(followUpReminderTime)
                            : null
                        }
                        onChange={(newValue) =>
                          setFollowUpReminderTime(
                            newValue ? newValue.format('YYYY-MM-DDTHH:mm') : null
                          )
                        }
                        minDateTime={dayjs()} // optional: prevent selecting past dates
                        desktopModeMediaQuery="@media (max-width: 0px)" // mobile picker
                        slotProps={{
                          textField: {
                            size: "small",
                            error: !!errors?.followUpReminderTime,
                          },
                        }}
                      />
                    </LocalizationProvider>

                    <FormHelperText
                      sx={{ fontSize: "13px", color: "error.main" }}
                    >
                      {errors?.followUpReminderTime}
                    </FormHelperText>
                  </Box>
                  <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                    {/* Buttons for pre-defined options */}
                    <Button
                      variant={
                        selectedFollowUp === "1_week" ? "contained" : "outlined"
                      }
                      color="main"
                      onClick={() =>
                        setFollowUpReminderTime(
                          calculateNewFollowUpTime("1_week")
                        )
                      }
                    >
                      1 Week
                    </Button>
                    <Button
                      variant={
                        selectedFollowUp === "2_weeks"
                          ? "contained"
                          : "outlined"
                      }
                      color="main"
                      onClick={() =>
                        setFollowUpReminderTime(
                          calculateNewFollowUpTime("2_weeks")
                        )
                      }
                    >
                      2 Weeks
                    </Button>
                    <Button
                      variant={
                        selectedFollowUp === "1_month"
                          ? "contained"
                          : "outlined"
                      }
                      color="main"
                      onClick={() =>
                        setFollowUpReminderTime(
                          calculateNewFollowUpTime("1_month")
                        )
                      }
                    >
                      1 Month
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        {/* )
        }         */}
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
          }}
          className="dialog-actions-dense"
        >
          <Box>
            <Button variant="contained" color="main" onClick={handleCreateLog}>
              {logData ? "Update Log" : "Create Log"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        handleClose={() => setShowUnsavedDialog(false)}
        handleDiscard={handleDiscardChanges}
      />
      </>
    );
}

export default AddCommunicationLog